import type { Layout } from '../../../data/types';
import type { Ring, Vec2 } from '../../../geo/types';
import { centroid, pointInPolygon, pointInRing, ringBounds } from '../../../geo/polygon';
import { createPolygonIndex } from '../../../geo/polygonIndex';
import { createSpatialHash } from '../../../geo/spatialHash';
import type { SiteModel } from '../../../site/types';
import type { PlannedLots } from '../../fabric/fabricLots';
import { landmarkObstacleRings } from '../../fabric/landmarks/landmarkObstacles';
import { collectFortificationReserved } from '../../fabric/fortifications/reserved';
import { layoutWaterTest } from '../../fabric/fortifications/moat';
import { joinGates } from './gateWays';
import { lastageYardGround } from './workGround';

/**
 * The ground the crowd walks: every street, alley, quay and square of the layout and every bridge,
 * as points a few metres apart joined where a body can pass between them. The cast's own street
 * graph (streetGraph.ts) keeps a walker's whole circle clear of every stoep and so leaves out most
 * alleys and all bridges; it falls into 151 pieces. Ten thousand residents have to reach the Dam,
 * the quays and the churches from anywhere, so this net follows the middle of each way and asks
 * only that the middle is footing.
 */
export const WALK_NET = {
  spacingM: 6,
  /** A square is covered by points this far apart, near enough to join their neighbours. */
  squareM: 5,
  bridgeSpacingM: 4,
  /** A bridge's line is run this far past its ends, onto the street it lands on. */
  bridgeReachM: 5,
  bridgeRailM: 0.9,
  /** Two ways are joined where their points come this near. */
  joinM: 5.5,
  /** The surfaces of two streets do not always meet: a join may cross this much open ground that
   *  is not paved. */
  seamM: 1.5,
  /** A way that ends short of every other is joined to the nearest point it can reach this far. */
  endReachM: 16,
  sampleM: 0.75,
  /** How far to either side of the middle a walker may keep, tried from the widest. */
  lanesM: [2.4, 1.8, 1.3, 0.9, 0.5],
} as const;

/** The net as it is baked: `nodes` is x, y and the free half-width of each point; `edges` pairs. */
export interface BakedWalkNet {
  readonly nodes: readonly number[];
  readonly edges: readonly number[];
}

export interface WalkNet {
  readonly count: number;
  readonly x: Float32Array;
  readonly y: Float32Array;
  /** How far to either side of the point a walker may keep. */
  readonly half: Float32Array;
  /** Each point's neighbours run from start[i] to start[i + 1] in `to`, with their lengths. */
  readonly start: Int32Array;
  readonly to: Int32Array;
  readonly length: Float32Array;
  /** Which connected piece of the net each point belongs to. */
  readonly piece: Int32Array;
}

type Footing = (p: Vec2) => boolean;

/** Two tests of a place: `paved`, on a street, a square, a bridge, the dam or worked ground and
 *  in no building; and `open`, in no building and not in the water, as the few steps from a door
 *  to its street. `worked` is the ground that is walked all over though no street crosses it. */
export interface Ground {
  readonly paved: Footing;
  readonly open: Footing;
  readonly worked?: readonly Ring[];
}

export function groundOf(layout: Layout, site: SiteModel, lots: PlannedLots): Ground {
  const streets = createPolygonIndex(site.streets, 16);
  const yard = lastageYardGround();
  const squares = [...layout.squares.map((square) => square.polygon as Ring), yard.ring];
  const decks = site.bridges.flatMap((bridge) => bridge.deck);
  const forts = layout.fortifications;
  const solid: readonly Ring[] = [
    ...[...lots.houses, ...lots.backhouses].map((lot) => lot.corners as unknown as Ring),
    ...lots.gangen.flatMap((gang) => gang.kamers.map((kamer) => kamer.corners as unknown as Ring)),
    ...layout.landmarks.flatMap(landmarkObstacleRings),
    ...collectFortificationReserved(
      forts.walls,
      forts.towers,
      forts.gates,
      layoutWaterTest(layout.water)
    ).map((reserved) => reserved.polygon.outer),
    ...yard.solid,
  ];
  const hash = createSpatialHash<Ring>(16);
  for (const ring of solid) hash.insert(ring, ringBounds(ring));
  const built = (p: Vec2): boolean =>
    hash
      .query({ minX: p[0], maxX: p[0], minY: p[1], maxY: p[1] })
      .some((ring) => pointInRing(p, ring));
  const decked = (p: Vec2): boolean =>
    decks.some((deck) => pointInPolygon(p, deck)) ||
    site.damDecks.some((deck) => pointInRing(p, deck));
  return {
    paved: (p) =>
      !built(p) &&
      (decked(p) ||
        (!site.waterIndex.contains(p) &&
          (streets.contains(p) || squares.some((ring) => pointInRing(p, ring))))),
    open: (p) => !built(p) && (decked(p) || !site.waterIndex.contains(p)),
    worked: [yard.ring],
  };
}

/** Whether every step of the straight line from `a` to `b` passes the test. */
export function passable(a: Vec2, b: Vec2, footing: Footing): boolean {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const steps = Math.max(1, Math.ceil(length / WALK_NET.sampleM));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    if (!footing([a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t])) return false;
  }
  return true;
}

/* A way's line as points no further apart than `spacing`, its own corners among them. */
function along(line: readonly Vec2[], spacing: number): Vec2[] {
  const points: Vec2[] = [];
  line.forEach((a, i) => {
    const b = line[i + 1];
    if (!b) {
      points.push(a);
      return;
    }
    const steps = Math.max(1, Math.ceil(Math.hypot(b[0] - a[0], b[1] - a[1]) / spacing));
    for (let j = 0; j < steps; j++)
      points.push([a[0] + ((b[0] - a[0]) * j) / steps, a[1] + ((b[1] - a[1]) * j) / steps]);
  });
  return points;
}

function bridgeLine(a: Vec2, b: Vec2): Vec2[] {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const [dx, dy] = [(b[0] - a[0]) / length, (b[1] - a[1]) / length];
  const reach = WALK_NET.bridgeReachM;
  return [
    [a[0] - dx * reach, a[1] - dy * reach],
    [b[0] + dx * reach, b[1] + dy * reach],
  ];
}

const ROUND: readonly Vec2[] = Array.from({ length: 8 }, (_, k) => [
  Math.cos((k * Math.PI) / 4),
  Math.sin((k * Math.PI) / 4),
]);

/* The widest lane to both sides of a point on a way running along `dir` that is all footing. A
   point of a square has no way through it, and its room is asked all round. */
function freeHalf(p: Vec2, dir: Vec2, footing: Footing): number {
  const sides: readonly Vec2[] =
    dir[0] === 0 && dir[1] === 0
      ? ROUND
      : [
          [-dir[1], dir[0]],
          [dir[1], -dir[0]],
        ];
  for (const lane of WALK_NET.lanesM) {
    const reach = lane + 0.35;
    if (sides.every((side) => footing([p[0] + side[0] * reach, p[1] + side[1] * reach])))
      return lane;
  }
  return 0;
}

/** How far round a place a figure may stand on paved ground. */
export function roomAt(p: Vec2, ground: Ground): number {
  return freeHalf(p, [0, 0], ground.paved);
}

/* Whether a body can pass from one way's point to another's: nowhere through a building or the
   water, and over no more unpaved ground than a seam. */
function joinable(a: Vec2, b: Vec2, ground: Ground): boolean {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const steps = Math.max(1, Math.ceil(length / WALK_NET.sampleM));
  let unpaved = 0;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const p: Vec2 = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    if (ground.paved(p)) continue;
    if (!ground.open(p) || ++unpaved * (length / steps) > WALK_NET.seamM) return false;
  }
  return true;
}

interface Draft {
  readonly nodes: number[];
  readonly edges: number[];
}

function addWay(draft: Draft, points: readonly Vec2[], footing: Footing, widest = Infinity): void {
  let previous = -1;
  points.forEach((p, i) => {
    if (!footing(p)) {
      previous = -1;
      return;
    }
    const next = points[i + 1] ?? p,
      before = points[i - 1] ?? p;
    const span = Math.hypot(next[0] - before[0], next[1] - before[1]) || 1;
    const dir: Vec2 = [(next[0] - before[0]) / span, (next[1] - before[1]) / span];
    const index = draft.nodes.length / 3;
    draft.nodes.push(p[0], p[1], Math.min(widest, freeHalf(p, dir, footing)));
    if (previous >= 0) {
      const from: Vec2 = [draft.nodes[previous * 3]!, draft.nodes[previous * 3 + 1]!];
      if (passable(from, p, footing)) draft.edges.push(previous, index);
    }
    previous = index;
  });
}

/* Joins the ways where they meet: each point to the points of other ways near it. Then each end
   still on a piece of its own is joined to the nearest point of another piece that it can reach:
   a quay's line often stops some metres short of the street it runs into. */
function joinWays(draft: Draft, ground: Ground): void {
  const count = draft.nodes.length / 3;
  const at = (i: number): Vec2 => [draft.nodes[i * 3]!, draft.nodes[i * 3 + 1]!];
  const hash = createSpatialHash<number>(WALK_NET.endReachM);
  const degree = new Uint16Array(count);
  const parent = Int32Array.from({ length: count }, (_, i) => i);
  const find = (i: number): number => {
    while (parent[i] !== i) i = parent[i] = parent[parent[i]!]!;
    return i;
  };
  const joined = new Set<number>();
  const join = (i: number, j: number): void => {
    joined.add(Math.min(i, j) * count + Math.max(i, j));
    degree[i]!++;
    degree[j]!++;
    parent[find(i)] = find(j);
  };
  for (let i = 0; i < count; i++) {
    const [x, y] = at(i);
    hash.insert(i, { minX: x, maxX: x, minY: y, maxY: y });
  }
  for (let e = 0; e < draft.edges.length; e += 2) join(draft.edges[e]!, draft.edges[e + 1]!);
  /* The points within `reach` of point i, nearest first. */
  const near = (i: number, reach: number): [number, number][] => {
    const p = at(i);
    const found: [number, number][] = [];
    for (const j of hash.query({
      minX: p[0] - reach,
      maxX: p[0] + reach,
      minY: p[1] - reach,
      maxY: p[1] + reach,
    })) {
      const q = at(j);
      const gap = Math.hypot(q[0] - p[0], q[1] - p[1]);
      if (gap <= reach && gap > 1e-6) found.push([gap, j]);
    }
    return found.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  };
  for (let i = 0; i < count; i++)
    for (const [, j] of near(i, WALK_NET.joinM)) {
      if (j <= i || joined.has(i * count + j) || !joinable(at(i), at(j), ground)) continue;
      join(i, j);
      draft.edges.push(i, j);
    }
  for (let i = 0; i < count; i++) {
    if (degree[i]! > 1) continue;
    const j = near(i, WALK_NET.endReachM).find(
      ([, other]) => find(other) !== find(i) && joinable(at(i), at(other), ground)
    )?.[1];
    if (j === undefined) continue;
    join(i, j);
    draft.edges.push(i, j);
  }
}

/** Plans the net from the layout. It takes a few hundred milliseconds, so it is baked. */
export function planWalkNet(
  layout: Layout,
  ground: Ground
): { readonly nodes: number[]; readonly edges: number[] } {
  const footing = ground.paved;
  const draft: Draft = { nodes: [], edges: [] };
  for (const street of layout.streets)
    addWay(draft, along(street.centerline, WALK_NET.spacingM), footing);
  // A deck's outline takes in its rails, so a walker keeps `bridgeRailM` inside it.
  for (const bridge of layout.bridges)
    addWay(
      draft,
      along(bridgeLine(bridge.a, bridge.b), WALK_NET.bridgeSpacingM),
      footing,
      Math.max(0, bridge.width / 2 - WALK_NET.bridgeRailM)
    );
  // The Dam's crest is ground as a square is: the fish market stands on it.
  const crests = layout.landmarks.filter((l) => l.kind === 'dam').map((dam) => dam.footprint);
  for (const ring of [
    ...layout.squares.map((square) => square.polygon as Ring),
    ...crests,
    ...(ground.worked ?? []),
  ]) {
    const { minX, minY, maxX, maxY } = ringBounds(ring);
    const centre = centroid(ring);
    for (let y = minY; y <= maxY; y += WALK_NET.squareM)
      for (let x = minX; x <= maxX; x += WALK_NET.squareM)
        if (pointInRing([x, y], ring)) addWay(draft, [[x, y]], footing);
    addWay(draft, [centre], footing);
  }
  joinWays(draft, ground);
  joinGates(draft, layout.fortifications.gates);
  return draft;
}

/** The baked net as typed arrays, with each point's neighbours filed and its piece found. */
export function restoreWalkNet(baked: BakedWalkNet): WalkNet {
  const count = baked.nodes.length / 3;
  const x = new Float32Array(count),
    y = new Float32Array(count),
    half = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    x[i] = baked.nodes[i * 3]!;
    y[i] = baked.nodes[i * 3 + 1]!;
    half[i] = baked.nodes[i * 3 + 2]!;
  }
  const start = new Int32Array(count + 1);
  for (const node of baked.edges) start[node + 1]!++;
  for (let i = 0; i < count; i++) start[i + 1]! += start[i]!;
  const next = start.slice(0, count);
  const to = new Int32Array(baked.edges.length),
    length = new Float32Array(baked.edges.length);
  for (let e = 0; e < baked.edges.length; e += 2) {
    const [a, b] = [baked.edges[e]!, baked.edges[e + 1]!];
    const gap = Math.hypot(x[b]! - x[a]!, y[b]! - y[a]!);
    to[next[a]!] = b;
    length[next[a]!++] = gap;
    to[next[b]!] = a;
    length[next[b]!++] = gap;
  }
  const piece = new Int32Array(count).fill(-1);
  let pieces = 0;
  for (let i = 0; i < count; i++) {
    if (piece[i] !== -1) continue;
    const open = [i];
    piece[i] = pieces;
    while (open.length) {
      const at = open.pop()!;
      for (let e = start[at]!; e < start[at + 1]!; e++) {
        if (piece[to[e]!] !== -1) continue;
        piece[to[e]!] = pieces;
        open.push(to[e]!);
      }
    }
    pieces++;
  }
  return { count, x, y, half, start, to, length, piece };
}

/** The point of the net nearest a place, within `reach` metres of it, or -1. */
export function nearestNode(net: WalkNet, p: Vec2, reach: number): number {
  let best = -1,
    gap = reach;
  for (let i = 0; i < net.count; i++) {
    const d = Math.hypot(net.x[i]! - p[0], net.y[i]! - p[1]);
    if (d < gap) {
      gap = d;
      best = i;
    }
  }
  return best;
}
